<template>
  <ClientOnly>
    <video
      ref="videoPlayer"
      :poster="poster"
      class="video-js h-full object-cover rounded-lg duration-300"
      :class="[isModal ? 'm-auto w-auto align-center' : 'w-full mx-auto']"
      controlslist="nofullscreen nodownload noremoteplayback noplaybackrate"
      loop
      muted
      autoplay
      playsinline
    />
  </ClientOnly>
</template>
<script lang="ts" setup>
import Hls from 'hls.js'

const {
  source = '',
  sourceStream = '',
  poster = '',
  isModal = false,
} = defineProps<{
  source: string
  sourceStream?: string
  poster: string
  isModal?: boolean
}>()

const videoPlayer = ref<HTMLVideoElement | null>(null) // ref to video element on page
const hlsRef = ref<Hls | null>(null) // ref to hls instance

// trying to load stream video
const loadVideo = (playerRefValue: HTMLVideoElement | null) => {
  if (hlsRef.value || !playerRefValue) return

  if (!Hls.isSupported()) {
    // Fallback for old browsers
    return loadLegacyVideo(playerRefValue)
  }

  const hls = new Hls() // run hls.js
  hls.loadSource(sourceStream) // load source
  hls.attachMedia(playerRefValue) // attach it to player

  hls.on(Hls.Events.ERROR, function (_event, data) {
    if (data.fatal) {
      hls.destroy()
      loadLegacyVideo(playerRefValue)
    }
  })

  hlsRef.value = hls // store hls instance
}

const loadLegacyVideo = (playerRefValue: HTMLVideoElement | null) => {
  if (!playerRefValue) return
  playerRefValue.src = source
}

// Watch for when the video element becomes available
watch(
  videoPlayer,
  (newVideoElement) => {
    if (newVideoElement && !hlsRef.value) {
      loadVideo(newVideoElement)
    }
  },
  { immediate: true }
)

onMounted(async () => {
  // Wait for the next tick to ensure DOM is ready
  await nextTick()

  // If video element is already available, load video
  if (videoPlayer.value && !hlsRef.value) {
    loadVideo(videoPlayer.value)
  }
})

onUnmounted(() => {
  // destroy hls instance
  if (hlsRef.value) {
    hlsRef.value.destroy()
    hlsRef.value = null
  }
})
</script>
